import React, { useState, useEffect, useContext, useCallback } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import FavoriteTwoToneIcon from '@mui/icons-material/FavoriteTwoTone';
import List from '@mui/material/List';
import { Link } from "react-router-dom";
import { Button } from '@mui/material';
import { LOCALES } from '../../i18n';
import { AppContext } from '../context';
import { updateSessionFavorite, getSessionReponses } from '../../services/requestHandler';
import translate from '../../i18n/translate';
import { SessionContext } from '../../services/SessionProvider';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import DescriptionIcon from '@mui/icons-material/Description';

const colorDefault = "black";
const colorFavorite = "#C21807";
const default_color = "#8EBF55";
const EAST = 3600000 * -5;

function getFormatedDate(date = "2022-11-28T21:21:54.624Z") {
    const parsedDateTemp = new Date(date);
    const parsedDate = new Date(parsedDateTemp.getTime() + EAST);
    const dayName_fr = parsedDate.toLocaleDateString('fr-CA', { weekday: 'long' });
    const monthName_fr = parsedDate.toLocaleDateString('fr-CA', { month: 'long' });
    const dayName_en = parsedDate.toLocaleDateString('en-US', { weekday: 'long' });
    const monthName_en = parsedDate.toLocaleDateString('en-US', { month: 'long' });
    const dayNumber = parsedDate.getDate();
    const yearNumber = parsedDate.getFullYear();
    const timeHours = parsedDate.getHours();
    let timeMinutes = parsedDate.getMinutes();
    if (timeMinutes < 10) timeMinutes = `0${timeMinutes}`;

    return {
        contenu: `${dayName_fr} ${dayNumber} ${monthName_fr} ${yearNumber} ${timeHours}:${timeMinutes}`,
        contenu_en: `${dayName_en} ${monthName_en} ${dayNumber} ${yearNumber} ${timeHours}:${timeMinutes}`,
    };
}

export default function CarteHistorique({ historique = {}, needReloadComponent, reloadComponent }) {
    const { state } = useContext(AppContext);
    const { getDevice } = useContext(SessionContext);
    const [initiales, setInitiales] = useState(null);
    const [isFavorite, setIsFavorite] = useState(historique.favoris);
    const [favorisIconColor, setFavorisIconColor] = useState(colorDefault);
    const [strategieFinale, setstrategieFinale] = useState([]);
    const [sessionsReponses, setSessionReponses] = useState(null);
    const [sessionDate, setSessionDate] = useState({ contenu: "", contenu_en: "" });
    const [isFrench, setIsFrench] = useState(true);

    useEffect(() => {
        if (isFavorite) {
            setFavorisIconColor(colorFavorite);
        } else {
            setFavorisIconColor(colorDefault);
        }

        if (Array.isArray(historique.parent_session_reponse) && historique.parent_session_reponse.length > 0) {
            const lastReponse = historique.parent_session_reponse[historique.parent_session_reponse.length - 1];
            if (lastReponse?.reponse?.strategies_list?.length > 0) {
                setstrategieFinale(lastReponse.reponse.strategies_list[0]);
            }
        }

        setIsFrench(state.locale === LOCALES.FRENCH);
    }, [isFavorite, state.locale, historique]);

    useEffect(() => {
        setSessionDate(getFormatedDate(historique.create_time));
    }, [historique.create_time]);

    const getSessionInfos = useCallback(async () => {
        const data = await getSessionReponses(historique.session_id);
        const dataReponses = [];

        if (Array.isArray(data) && data.length > 0) {
            const customInit = data.find((r) =>
                r?.reponse?.contenu?.includes("Pour faciliter le repérage ultérieur, indiquer vos initiales – celles du patient (p. ex. CV-HC)") ||
                r?.reponse?.contenu_en?.includes("To help with later identification, indicate your initials – those of the patient (e.g., CV-HC)")
            );
            if (customInit && customInit.custom_reponse_contenu) {
                setInitiales(customInit.custom_reponse_contenu);
            }

            for (let i = 0; i < data.length; i++) {
                let reponse = data[i]?.reponse;
                if (reponse && data[i]?.custom_reponse_contenu) {
                    const customValue = ` : ${data[i].custom_reponse_contenu}`;
                    reponse.contenu = `${reponse.contenu}${customValue}`;
                    reponse.contenu_en = `${reponse.contenu_en || reponse.contenu}${customValue}`;
                }
                if (reponse) dataReponses.push(reponse);
                if (checkIfStringNeedsFormatting(data[i]?.question?.contenu)) {
                    let contenu = formatString(data[i].question.contenu, true, dataReponses);
                    let contenu_en = formatString(data[i].question.contenu_en, false, dataReponses);
                    data[i].question.contenu = contenu;
                    data[i].question.contenu_en = contenu_en;
                }
            }
        }

        setSessionReponses(data);
    }, [historique.session_id]);

    useEffect(() => {
        getSessionInfos();
    }, [getSessionInfos]);

    async function changeSessionFavorite() {
        await updateSessionFavorite(historique.session_id, !isFavorite);
        setIsFavorite(!isFavorite);
        needReloadComponent(!reloadComponent);
    }

    const generateWordFile = async () => {
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        ...((sessionsReponses || []).map((session, index) => (
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${index + 1}. ${isFrench ? session?.question?.contenu : session?.question?.contenu_en}`,
                                        bold: true,
                                        size: 28,
                                    }),
                                    new TextRun({
                                        text: "\n" + (isFrench ? session?.reponse?.contenu : session?.reponse?.contenu_en),
                                        size: 24,
                                    }),
                                ],
                                spacing: { after: 400 },
                            })
                        ))),
                        new Paragraph({ text: "Espace pour ajouter vos notes :", bold: true, spacing: { after: 200 } }),
                        new Paragraph({ text: "________________________________________________________________________________", spacing: { after: 200 } }),
                        new Paragraph({ text: "________________________________________________________________________________", spacing: { after: 200 } }),
                    ],
                },
            ],
        });
        const blob = await Packer.toBlob(doc);
        saveAs(blob, `session_${historique.session_id}.docx`);
    };

    return (
        <div style={{ marginTop: "2rem" }}>
            <Card sx={{ width: "100%", backgroundColor: default_color, display: 'inline-block' }}>
                <CardContent>
                    <div style={{ display: "flex" }}>
                        <div style={{ alignSelf: "center" }}>{isFrench ? sessionDate.contenu : sessionDate.contenu_en}</div>
                        <div style={{ marginLeft: "auto" }}>
                            {(!isFavorite && getDevice) && "AjouterFavoris"}
                            {(isFavorite && getDevice) && "EnleverFavoris"}
                            <IconButton onClick={changeSessionFavorite} aria-label="add to favorites">
                                <FavoriteTwoToneIcon sx={{ color: favorisIconColor }} />
                            </IconButton>
                            <IconButton onClick={generateWordFile} aria-label="download word">
                                <DescriptionIcon sx={{ color: "black" }} />
                            </IconButton>
                        </div>
                    </div>
                    <List>
                        <h2> - Initiales : {initiales} </h2>
                        {(sessionsReponses || []).map((session, index) => (
                            <div key={index}>
                                <h2>{index + 1} : {isFrench ? session?.question?.contenu : session?.question?.contenu_en}</h2>
                                <h3>{isFrench ? session?.reponse?.contenu : session?.reponse?.contenu_en}</h3>
                            </div>
                        ))}
                    </List>
                    <div style={{ display: "flex" }}>
                        <Link to={`/strategie/${strategieFinale?.reponse_id}`} style={{ textDecoration: 'none' }}>
                            {(historique.current_status === "A fonctionné" || historique.current_status === "A échoué") && (
                                <Button variant="contained" style={{ marginRight: "0.5rem", backgroundColor: "#307B5B" }}>
                                    {translate("historique.voirStrategie")}
                                </Button>
                            )}
                        </Link>
                        <div style={{ marginLeft: "auto" }}>
                            {historique.current_status === "A fonctionné" && <p>{translate("historique.fonctionne")}</p>}
                            {historique.current_status === "A échoué" && <p>{translate("historique.non_fonctionne")}</p>}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

const checkIfStringNeedsFormatting = (string) => /{[1-5]}/.test(string);
const nullFormat = (string) => string ?? "null";
const formatString = (string, isFrench, dataReponses = []) =>
    string.replace(/{(\d)}/g, (_, index) => {
        const replacedText = dataReponses[parseInt(index) - 1];
        return replacedText ? (isFrench ? nullFormat(replacedText?.contenu) : nullFormat(replacedText?.contenu_en)) : "null";
    });
